"use client";

import { useEffect, useMemo, useState } from "react";
import {
  getTcruziRunFiles,
  tcruziRunManifest,
} from "../tcruzi-runs";

type Scope = "all" | "atlas" | "validation";

const copy = {
  zh: {
    search: "搜索样本、阶段、ERS / ERX / ERR 登录号或 FASTQ 文件名",
    allSamples: "全部样本",
    atlas: "图谱样本 1–7",
    validation: "独立验证样本 8",
    sourceSamples: "来源样本",
    experiments: "实验",
    runs: "测序运行",
    files: "FASTQ 条目",
    sample: "样本",
    experiment: "实验",
    technicalGroup: "技术重复组",
    stage: "论文阶段",
    scopeAtlas: "纳入生命周期图谱",
    scopeValidation: "48 小时无鞭毛体验证",
    runFiles: "查看该运行的 FASTQ 文件",
    fileRole: "文件作用",
    openRun: "在 ENA 打开运行",
    noResults: "没有符合当前筛选条件的运行。",
    exactSource: "以下字段来自作者提交的 SDRF；中文阶段名称为本站对应翻译。",
    manifest: "下载聚合后的 JSON 清单",
    sdrf: "下载原始 SDRF",
    records: "条记录",
    reset: "清除筛选",
    read1: "R1：细胞条形码 + UMI",
    read2: "R2：cDNA 序列",
    index1: "I1：样本条形码",
    index2: "I2：第二样本条形码（混合样本）",
  },
  en: {
    search: "Search sample, stage, ERS / ERX / ERR accession or FASTQ name",
    allSamples: "All samples",
    atlas: "Atlas samples 1–7",
    validation: "Independent validation sample 8",
    sourceSamples: "source samples",
    experiments: "experiments",
    runs: "sequencing runs",
    files: "FASTQ entries",
    sample: "Sample",
    experiment: "Experiment",
    technicalGroup: "Technical replicate group",
    stage: "Submitted stage",
    scopeAtlas: "Included in lifecycle atlas",
    scopeValidation: "48 h amastigote validation",
    runFiles: "Show FASTQ files for this run",
    fileRole: "File role",
    openRun: "Open run in ENA",
    noResults: "No sequencing runs match these filters.",
    exactSource:
      "The fields below come from the author-submitted SDRF; Chinese stage labels are TrypCell translations.",
    manifest: "Download aggregated JSON manifest",
    sdrf: "Download source SDRF",
    records: "records",
    reset: "Clear filters",
    read1: "R1: cell barcode + UMI",
    read2: "R2: cDNA sequence",
    index1: "I1: sample barcode",
    index2: "I2: second sample barcode (mixed samples)",
  },
};

function enaUrl(accession: string) {
  return `https://www.ebi.ac.uk/ena/browser/view/${accession}`;
}

export function TcruziRunBrowser({
  manifestDownloadUrl = "/api/tcruzi-runs?download=1",
}: {
  manifestDownloadUrl?: string;
} = {}) {
  const [language, setLanguage] = useState<"zh" | "en">("zh");
  const [scope, setScope] = useState<Scope>("all");
  const [sampleId, setSampleId] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    const handleLanguage = (event: Event) => {
      setLanguage((event as CustomEvent<"en" | "zh">).detail);
    };
    window.addEventListener("trypcell-language", handleLanguage);
    setLanguage(document.body.dataset.language === "en" ? "en" : "zh");
    return () =>
      window.removeEventListener("trypcell-language", handleLanguage);
  }, []);

  const t = copy[language];

  const scopedSamples = useMemo(
    () =>
      tcruziRunManifest.samples.filter(
        (sample) => scope === "all" || sample.scope === scope,
      ),
    [scope],
  );

  const visibleRuns = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return scopedSamples.flatMap((sample) =>
      sample.runs
        .filter(() => sampleId === "all" || sample.id === sampleId)
        .filter((run) => {
          if (!needle) return true;
          const files = getTcruziRunFiles(run);
          return [
            sample.sourceName,
            sample.enaSample,
            sample.bioSample,
            sample.cellType,
            sample.cellTypeZh,
            sample.experiment,
            sample.technicalReplicateGroup ?? "",
            run.accession,
            run.assay,
            ...files.map((file) => file.name),
          ]
            .join(" ")
            .toLowerCase()
            .includes(needle);
        })
        .map((run) => ({ sample, run, files: getTcruziRunFiles(run) })),
    );
  }, [query, sampleId, scopedSamples]);

  const visibleSamples = new Set(
    visibleRuns.map(({ sample }) => sample.enaSample),
  ).size;
  const visibleExperiments = new Set(
    visibleRuns.map(({ sample }) => sample.experiment),
  ).size;
  const visibleFiles = visibleRuns.reduce(
    (sum, record) => sum + record.files.length,
    0,
  );

  const chooseScope = (nextScope: Scope) => {
    setScope(nextScope);
    setSampleId("all");
  };

  const clearFilters = () => {
    setScope("all");
    setSampleId("all");
    setQuery("");
  };

  return (
    <div className="run-browser">
      <section className="run-source-banner">
        <div>
          <span>VERIFIED SDRF · SHA-256</span>
          <code>{tcruziRunManifest.source.sdrfSha256}</code>
          <p>{t.exactSource}</p>
        </div>
        <div className="run-source-actions">
          <a
            href={tcruziRunManifest.source.sdrfUrl}
            target="_blank"
            rel="noreferrer"
          >
            {t.sdrf} ↗
          </a>
          <a href={manifestDownloadUrl} download>
            {t.manifest} ↓
          </a>
        </div>
      </section>

      <section className="run-library-strip" aria-label="Library metadata">
        <div>
          <span>Library</span>
          <strong>{tcruziRunManifest.library.construction}</strong>
        </div>
        <div>
          <span>Layout</span>
          <strong>{tcruziRunManifest.library.layout}</strong>
        </div>
        <div>
          <span>Selection</span>
          <strong>{tcruziRunManifest.library.selection}</strong>
        </div>
        <div>
          <span>cDNA</span>
          <strong>R2 · 91 bp</strong>
        </div>
        <div>
          <span>Cell barcode</span>
          <strong>R1 · 16 bp</strong>
        </div>
        <div>
          <span>UMI</span>
          <strong>R1 · 12 bp</strong>
        </div>
      </section>

      <section className="run-controls">
        <label className="run-search">
          <span aria-hidden="true">⌕</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t.search}
          />
        </label>
        <div className="run-scope" aria-label="Filter by study scope">
          {[
            ["all", t.allSamples],
            ["atlas", t.atlas],
            ["validation", t.validation],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={scope === value ? "active" : ""}
              aria-pressed={scope === value}
              onClick={() => chooseScope(value as Scope)}
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      <section className="run-stats" aria-live="polite">
        {[
          [visibleSamples, t.sourceSamples],
          [visibleExperiments, t.experiments],
          [visibleRuns.length, t.runs],
          [visibleFiles, t.files],
        ].map(([value, label]) => (
          <div key={label}>
            <strong>{value}</strong>
            <span>{label}</span>
          </div>
        ))}
      </section>

      <section className="sample-selector">
        <button
          type="button"
          className={sampleId === "all" ? "active" : ""}
          aria-pressed={sampleId === "all"}
          onClick={() => setSampleId("all")}
        >
          <span>00</span>
          <strong>{t.allSamples}</strong>
          <small>{scopedSamples.length}</small>
        </button>
        {scopedSamples.map((sample) => (
          <button
            type="button"
            key={sample.id}
            className={sampleId === sample.id ? "active" : ""}
            aria-pressed={sampleId === sample.id}
            onClick={() => setSampleId(sample.id)}
          >
            <span>{sample.id.replace("sample-", "").padStart(2, "0")}</span>
            <strong>
              {language === "zh" ? sample.cellTypeZh : sample.cellType}
            </strong>
            <small>{sample.runs.length} ERR</small>
          </button>
        ))}
      </section>

      <section className="run-results">
        <div className="run-results-heading">
          <div>
            <strong>{visibleRuns.length}</strong> {t.records}
          </div>
          {scope !== "all" || sampleId !== "all" || query ? (
            <button type="button" onClick={clearFilters}>
              {t.reset}
            </button>
          ) : null}
        </div>

        <div className="run-record-list">
          {visibleRuns.map(({ sample, run, files }, index) => (
            <article className="run-record" key={run.accession}>
              <header>
                <div className="run-number">
                  {(index + 1).toString().padStart(2, "0")}
                </div>
                <div>
                  <span>
                    {sample.scope === "atlas"
                      ? t.scopeAtlas
                      : t.scopeValidation}
                  </span>
                  <h2>{run.accession}</h2>
                  <p>{run.assay}</p>
                </div>
                <a
                  href={enaUrl(run.accession)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t.openRun} ↗
                </a>
              </header>

              <div className="run-record-meta">
                <div>
                  <span>{t.sample}</span>
                  <strong>{sample.sourceName}</strong>
                  <p>
                    <a
                      href={enaUrl(sample.enaSample)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {sample.enaSample} ↗
                    </a>
                    <a
                      href={enaUrl(sample.bioSample)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {sample.bioSample} ↗
                    </a>
                  </p>
                </div>
                <div>
                  <span>{t.stage}</span>
                  <strong>
                    {language === "zh" ? sample.cellTypeZh : sample.cellType}
                  </strong>
                  {language === "zh" ? <p>{sample.cellType}</p> : null}
                </div>
                <div>
                  <span>{t.experiment}</span>
                  <strong>
                    <a
                      href={enaUrl(sample.experiment)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {sample.experiment} ↗
                    </a>
                  </strong>
                  <p>
                    {t.technicalGroup}:{" "}
                    {sample.technicalReplicateGroup ?? "not supplied"}
                  </p>
                </div>
              </div>

              <details className="fastq-files">
                <summary>
                  <span>{t.runFiles}</span>
                  <strong>{files.length} FASTQ</strong>
                </summary>
                <div className="fastq-legend">
                  <span>{t.read1}</span>
                  <span>{t.read2}</span>
                  <span>{t.index1}</span>
                  {run.dualIndex ? <span>{t.index2}</span> : null}
                </div>
                <div className="fastq-file-list">
                  {files.map((file) => (
                    <a
                      key={file.name}
                      href={file.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span>
                        <strong>{file.readIndex}</strong>
                        <small>{file.readType}</small>
                      </span>
                      <code>{file.name}</code>
                      <b aria-hidden="true">↓</b>
                    </a>
                  ))}
                </div>
              </details>
            </article>
          ))}
          {visibleRuns.length === 0 ? (
            <div className="run-empty">{t.noResults}</div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
